/**
 * SchemaClient - Client for fetching CRM schema definitions from the backend.
 *
 * Provides access to association types, object types, property types, etc.
 */

import { apiCall } from '@/lib/http/client';

/**
 * Association type definition from backend schema.
 */
export interface AssociationType {
  association_type_name: string;
  label: string;
  source_entity_type_name: string;
  target_entity_type_name: string;
  reverse_association_type_name: string;
  cardinality: 'one_to_one' | 'one_to_many' | 'many_to_one' | 'many_to_many';
  value_read_access: 'all' | 'internal';
  value_write_access?: 'all' | 'internal';
  /**
   * `'none'` marks the association write-once: the first add is permitted, then
   * removal and re-pointing are rejected by the backend. Surfaces that offer an
   * edit affordance must consult `isAssociationValueFrozen` (see
   * `utils/associationEditability.ts`) rather than reading this directly. May be
   * missing from cached responses pre-rollout — treat missing as `'all'`.
   */
  value_removal_access?: 'all' | 'none';
  transitive_via: string[] | null;
  /** Entity kind of the source entity type (e.g. 'default', 'stage', 'pipeline'). Null for type-based associations. */
  source_entity_kind_name?: string | null;
  /** Entity kind of the target entity type (e.g. 'default', 'stage', 'pipeline'). Null for type-based associations. */
  target_entity_kind_name?: string | null;
  /**
   * Entity type names to EXCLUDE from kind-based resolution. Only meaningful when the
   * source side is kind-based. Example: `ahoy_contact_to_note` covers kind `person`
   * but excludes `ahoy_user`. May be missing from cached responses pre-rollout — default
   * to an empty array when reading.
   */
  source_excluded_entity_type_names?: string[];
  /** Same as source_excluded_entity_type_names but for the target side. */
  target_excluded_entity_type_names?: string[];
  /**
   * Server-declared: `true` when the shared backend sortability predicate accepts this
   * association type. The UI offers a column sort only when this is `true`, so it can never
   * offer a sort the backend rejects. May be missing from schema payloads cached before the
   * backend rollout (Redis per-epoch cache / persisted localStorage) — treat missing as not
   * sortable (fail closed).
   */
  sortable?: boolean;
}

/**
 * CRM object type definition from backend schema.
 */
export interface CrmObjectTypeSchema {
  entity_type_name: string;
  label: string;
  label_plural: string;
  description: string;
  icon?: string;
  allows_custom_properties: boolean;
  entity_kind: string[];
  /**
   * Whether a USER principal may create records of this type — the schema's
   * ahoy_object_created write access resolves to "all". False for
   * system-managed types (users, email threads, integrations…), whose
   * records only the system creates; the backend rejects user-sourced
   * creates on them. Orthogonal to per-user permission grants
   * (usePermissions). Optional only for cached pre-rollout responses —
   * treat missing as creatable (the backend still enforces).
   */
  user_creatable?: boolean;
  /**
   * The write event types the schema permits from a USER principal on this
   * type — the general form of `user_creatable`. A synced type (meetings,
   * email threads…) lists only its association events, or nothing at all.
   * Optional for the same cached-response reason as `user_creatable`; treat
   * missing as "every write event" (the backend still enforces).
   */
  writable_event_types?: string[];
}

/**
 * Select option for property types.
 */
export interface PropertyTypeSelectOption {
  value: string;
  label: string;
  description?: string;
}

/**
 * Subproperty schema definition for composite field types.
 */
export interface SubPropertySchema {
  subproperty_name: string;
  field_type_name: string;
  label: string;
  options?: { value: string; label: string; description?: string }[];
  value_write_access: 'all' | 'internal' | 'none';
  value_read_access: 'all' | 'internal';
  is_unique_value: boolean;
}

/**
 * Unit option for field types (e.g., currency units).
 */
export interface FieldTypeUnit {
  value: string;
  label: string;
  description?: string;
}

/**
 * Field type definition from backend schema.
 */
export interface FieldType {
  field_type_name: string;
  label: string;
  description: string;
  value_type_name: string;
  operators: string[];
  aggregations: string[];
  units?: FieldTypeUnit[];
  sortable?: boolean;
  type_use_access: 'all' | 'internal';
}

/**
 * Property type definition from backend schema.
 */
export interface PropertyType {
  entity_id: string;
  property_type_name: string;
  label: string;
  description?: string;
  field_type_name: string;
  is_required: boolean;
  is_unique_value: boolean;
  is_multi?: boolean;
  value_write_access: 'all' | 'internal' | 'none';
  value_read_access: 'all' | 'internal';
  value_update_access: 'all' | 'none';
  entity_type_name: string;
  options?: PropertyTypeSelectOption[];
  subproperties?: SubPropertySchema[];
  last_event_seq: number;
}

/**
 * Response for property type usage count.
 */
export interface PropertyTypeUsageCountResponse {
  count: number;
}

/**
 * Client for accessing CRM schema definitions.
 */
export class SchemaClient {
  constructor(private orgId: number) {}

  /**
   * Fetch all association types.
   */
  async getAssociationTypes(params?: {
    source_entity_type_name?: string;
  }): Promise<AssociationType[]> {
    const queryString = params?.source_entity_type_name
      ? `?source_entity_type_name=${encodeURIComponent(params.source_entity_type_name)}`
      : '';

    const url = `/api/orgs/${this.orgId}/crm-events/schemas/association-types/${queryString}`;
    const result = await apiCall<AssociationType[]>(url);
    // Handle 204 No Content by returning empty array
    return Array.isArray(result) ? result : [];
  }

  /**
   * Fetch all CRM object types.
   */
  async getObjectTypes(): Promise<CrmObjectTypeSchema[]> {
    const url = `/api/orgs/${this.orgId}/crm-events/schemas/object-types/`;
    return apiCall<CrmObjectTypeSchema[]>(url);
  }

  /**
   * Fetch property types, optionally filtered by entity type.
   *
   * @param entityTypeName - Optional entity type to filter by (e.g., 'ahoy_contact')
   * @returns Array of property type definitions
   */
  async getPropertyTypes(entityTypeName?: string): Promise<PropertyType[]> {
    let url = `/api/orgs/${this.orgId}/crm-events/schemas/property-types/`;

    if (entityTypeName) {
      url += `?entity_type_name=${encodeURIComponent(entityTypeName)}`;
    }

    const result = await apiCall<PropertyType[]>(url);
    // Handle 204 No Content by returning empty array
    return Array.isArray(result) ? result : [];
  }

  /**
   * Get the usage count for a specific property type.
   *
   * @param entityTypeName - The entity type name (e.g., 'ahoy_contact')
   * @param propertyTypeName - The property type name (e.g., 'ahoy_custom_field')
   * @returns The number of objects using this property type
   */
  async getPropertyTypeUsageCount(
    entityTypeName: string,
    propertyTypeName: string
  ): Promise<number> {
    const url = `/api/orgs/${this.orgId}/crm-events/schemas/property-types/${encodeURIComponent(entityTypeName)}/${encodeURIComponent(propertyTypeName)}/usage-count/`;
    const result = await apiCall<PropertyTypeUsageCountResponse>(url);
    return result.count;
  }

  /**
   * Fetch all field types from the schema.
   *
   * @returns Array of field type definitions
   */
  async getFieldTypes(): Promise<FieldType[]> {
    const url = `/api/orgs/${this.orgId}/crm-events/schemas/field-types/`;
    return apiCall<FieldType[]>(url);
  }
}

/**
 * Factory function for creating schema clients.
 */
export function createSchemaClient(orgId: number): SchemaClient {
  return new SchemaClient(orgId);
}
